import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { Slider } from 'antd';
import { colord } from 'colord';
import { createDefaultPositions, getColorsPalette } from '../../utils';
import SolidPicker from './SolidPicker';

export default function GradientPicker({ value, onChange }) {
  const mounted = useRef(false);
  const control = useRef(null);
  const [positions, setPositions] = useState([0, 100]);
  const [_colors, setColors] = useState([value[0], value[value.length - 1]]);
  const [activeIndex, setActiveIndex] = useState(0); // 0: start, 1: end
  const activeColor = useMemo(
    () => _colors[activeIndex],
    [_colors, activeIndex]
  );

  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 1 && !mounted.current) {
      // set slider positions from sdk data
      setColors([value[0], value[value.length - 1]]);
      const sdkPositions = createDefaultPositions(value);
      setPositions(sdkPositions);
      mounted.current = true;
    }
  }, [value]);

  const handleGradientColorChange = useCallback(
    (startColor, endColor, startPosition, endPosition) => {
      const colorPalette = getColorsPalette(
        startColor,
        endColor,
        startPosition,
        endPosition
      );

      const colorPaletteHex = [
        ...colorPalette.map((color) => colord(color).toHex()),
      ];
      onChange(colorPaletteHex);
    },
    []
  );

  const handleActiveColorChange = (rgba) => {
    const newColors = [..._colors];
    newColors[activeIndex] = rgba;
    setColors(newColors);
    handleGradientColorChange(
      newColors[0],
      newColors[newColors.length - 1],
      positions[0],
      positions[1]
    );
  };

  const setLinearGradient = (values) => {
    const [start, end] = values;
    setPositions([start, end]);
    handleGradientColorChange(_colors[0], _colors[1], start, end);
  };
  const background = useMemo(() => {
    const stopStrings = _colors.map((color, i) => {
      return `${color} ${positions[i]}%`;
    });
    return `linear-gradient(to right, ${stopStrings.join(', ')})`;
  }, [_colors, positions]);

  const handleSliderFocus = (e) => {
    if (e.target.classList.contains('ant-slider-handle-1')) {
      setActiveIndex(0);
    }
    if (e.target.classList.contains('ant-slider-handle-2')) {
      setActiveIndex(1);
    }
  };

  return (
    <>
      <Slider
        ref={control}
        style={{
          background,
          '--handle-1-color': value[0],
          '--handle-2-color': value[value.length - 1],
        }}
        value={positions}
        range
        className="color-picker__gradient-control"
        onChange={setLinearGradient}
        tooltip={{
          open: false,
        }}
        onFocus={handleSliderFocus}
      />
      <SolidPicker
        activeGradientIndex={activeIndex}
        value={
          activeColor.startsWith('rgba')
            ? activeColor
            : colord(activeColor).toRgbString()
        }
        gradientColor={value}
        onChange={handleActiveColorChange}
      />
    </>
  );
}
